import { useCurrentShop, useUser } from '#storefront/composables'
import type {
  DataLayerInitData,
  TrackingEvent,
  TrackingPayload,
} from '~/types/tracking'
import { getClientData } from '~/utils/clientDetection'
import { getEmailHash, getEmailHashMD5 } from '~/utils'
import { useMurId } from '~/composables/useMurId'

const SHOP_GENDER: 'male' | 'female' | 'other' | '' = ''

const useShopEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => void,
) => {
  const currentShop = useCurrentShop().value
  const currency = currentShop!.currency
  const locale = currentShop!.locale
  const shopId = currentShop!.shopId

  return {
    dataLayerInit: async () => {
      const { isLoggedIn, user } = await useUser()
      const { murId } = useMurId({
        user: user.value,
      })
      const birthDate = user.value?.birthDate
      const ageByDate =
        birthDate &&
        new Date().getFullYear() - new Date(birthDate).getFullYear()

      const md5 = await getEmailHashMD5(user.value?.email)
      const sha256 = await getEmailHash(user.value?.email)

      const clientData = getClientData()

      const payload = {
        original_location: {
          url: window?.location.href,
        },
        shop: {
          entity: 'SEU',
          country: currentShop?.countryCode,
          language: currentShop?.locale.split('-')?.[0]?.toUpperCase(),
          shop_version: 'NH_v2',
          origin: 'website',
          currency,
        },
        gender: user?.value?.gender || undefined,
        logged_in: user.value?.authentication?.type || undefined,
        login_status: isLoggedIn.value,
        mur_id: murId.value,
        eh: sha256 || undefined,
        ehm: md5 || undefined,
        age: ageByDate || 0,
        cuid: sha256 ? `${sha256}+salt` : undefined,
        customer_id: user.value?.id,
        client: clientData,
      } as DataLayerInitData

      return track('dataLayerInit', payload)
    },

    trackShopChange: () => {
      return track('shop_change', {
        shop_id: String(shopId),
        shop_gender: SHOP_GENDER,
        locale: locale.replace(/_/g, '-'),
        shop_currency: currency,
      })
    },
  }
}

export default useShopEvents
